import { MouseEvent } from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { JOB_STATUS } from "common/constants";
import { formatTimeDifference } from "common/utils/dates";
import { spacing } from "../../../stitches.config";
import { formatDateString } from "../../../utils/dates";
import { DownloadAnchor } from "../../Common/__styles__/downloadLink";
import Icon, { ICON_COLORS, Icons } from "../../Common/Icons";
import { IconNames } from "../../Common/Icons/LucideIcons";
import { ManualPaginationConfig } from "../../Common/Tables/hooks";
import Table from "../../Common/Tables/Table";
import { StyleVariant, Tag } from "../../Common/Tag";
import FileDisplay from "../../Inputs/FileDisplay";
import {
  ExpandedContainer,
  Info,
  StatusWrapper,
  TimeInfo,
  Title,
} from "./__styles__/JobsTable";

export interface RecentJob {
  id: string;
  name: string;
  error?: string;
  createdAt: string;
  stoppedAt?: string;
  csvDownloadHref?: string;
  disabled?: boolean;
  status: JOB_STATUS;
  icon: Icon;
  actionButton?: ({
    jobId,
    disabled,
  }: {
    jobId: string;
    disabled: boolean;
  }) => JSX.Element;
}

type Props = {
  loading: boolean;
  recentJobs: RecentJob[];
  manualPaginationConfig: ManualPaginationConfig;
  loadingText: string;
  noDataText: string;
};

const rowCanExpand = (row: Row<RecentJob>) =>
  !!row.original.error || !!row.original.csvDownloadHref;

export const JOB_STATUS_TO_TAG_INFO = {
  [JOB_STATUS.PROCESSING]: {
    label: "In progress",
    leftIconName: "loading",
    styleVariant: "neutral",
  },
  [JOB_STATUS.FAILURE]: {
    label: "Failed",
    leftIconName: "x-circle",
    styleVariant: "critical",
  },
  [JOB_STATUS.SUCCESS]: {
    leftIconName: "check-circle",
    label: "Success",
    styleVariant: "success",
  },
  [JOB_STATUS.UNKNOWN]: {
    leftIconName: "circle-alert",
    label: "Unknown",
    styleVariant: "warning",
  },
} as const satisfies Record<
  JOB_STATUS,
  {
    label: string;
    leftIconName: IconNames;
    styleVariant: StyleVariant;
  }
>;

const columns: Array<ColumnDef<RecentJob>> = [
  {
    accessorKey: "task",
    id: "task",
    cell: ({ row }) => {
      const icon = row.original.icon;
      const timeDifference =
        row.original.stoppedAt ?
          formatTimeDifference(
            new Date(row.original.stoppedAt),
            new Date(row.original.createdAt),
          )
        : null;
      return (
        <FileDisplay
          fileIcon={icon}
          titleText={row.original.name}
          expandable={rowCanExpand(row)}
          expanded={row.getIsExpanded()}
          onClick={row.getToggleExpandedHandler()}
        >
          <Icon icon={Icons.CALENDAR} color={ICON_COLORS.GREY_4} />
          <TimeInfo>
            <span>
              {formatDateString({
                format: "MM D, YYYY at HH:MM ZZ",
                dateString: row.original.createdAt,
              }).toLocaleString()}
            </span>
            {timeDifference && <span> · Completed in {timeDifference}</span>}
          </TimeInfo>
        </FileDisplay>
      );
    },
    meta: {
      getCellStyles: () => ({
        paddingTop: spacing.m.value,
        paddingBottom: spacing.m.value,
      }),
    },
  },
  {
    accessorKey: "status",
    id: "status",
    cell: ({ row }) => {
      const { actionButton } = row.original;
      const { leftIconName, styleVariant, label } =
        JOB_STATUS_TO_TAG_INFO[row.original.status];
      return (
        <StatusWrapper>
          <Tag leftIconName={leftIconName} styleVariant={styleVariant}>
            {label}
          </Tag>
          {actionButton ?
            actionButton({
              jobId: row.original.id,
              disabled: row.original.disabled ?? false,
            })
          : ""}
        </StatusWrapper>
      );
    },
  },
];

const renderSubComponent = (row: Row<RecentJob>) => {
  const { error, csvDownloadHref, disabled = false } = row.original;
  if (!error && !csvDownloadHref) return <></>;

  const onClickHandler = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
  };

  const errorProps = {
    title: "Error message",
    message: error,
  };

  const csvProps = {
    title: "CSV Download",
    message: (
      <DownloadAnchor
        as={"a"}
        disabled={disabled}
        tabIndex={0}
        href={csvDownloadHref}
        onClick={onClickHandler}
        download
      >
        Download .csv with all relevant errors
      </DownloadAnchor>
    ),
  };

  const { title, message } = csvDownloadHref ? csvProps : errorProps;

  return (
    <ExpandedContainer>
      <Title>{title}</Title>
      <Info>{message}</Info>
    </ExpandedContainer>
  );
};

const RecentJobsTable = ({
  recentJobs,
  manualPaginationConfig,
  loading,
  loadingText,
  noDataText,
}: Props) => {
  return (
    <Table<RecentJob>
      currentData={recentJobs}
      previousData={[]}
      columns={columns}
      loadingDetails={{
        loading,
        loadingText,
        noDataText,
      }}
      minRows={recentJobs.length}
      manualPaginationConfig={manualPaginationConfig}
      tableStyleDetails={{
        hasExpandableRows: true,
        hasFlushedSides: true,
      }}
      rowCanExpand={rowCanExpand}
      renderSubComponent={renderSubComponent}
      excludeTableHeader
    />
  );
};

export default RecentJobsTable;
